import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Clock,
  BadgePercent,
  Ship,
  Database,
  Landmark,
  Repeat2,
  Building2,
  ArrowUpRight,
  CheckCircle2,
  Globe2,
  ShieldCheck,
  Layers3,
  Route,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";

const benefits = [
  {
    title: "24-Hour Operations",
    text:
      "Round-the-clock operations enable investors to move faster, reduce delays and sustain continuous oil and gas logistics activities.",
    icon: Clock,
  },
  {
    title: "75% Duty Rebate",
    text:
      "Investors benefit from duty rebates when goods are exported from the free zone into Nigerian territory under applicable regulations.",
    icon: BadgePercent,
  },
  {
    title: "Direct Sea Access",
    text:
      "Strategic port-based locations provide direct sea access for cargo handling, logistics operations and regional distribution.",
    icon: Ship,
  },
  {
    title: "Computerised Inventory",
    text:
      "Modern inventory systems support transparency, tracking, control and efficient free zone operations.",
    icon: Database,
  },
  {
    title: "Tax Incentives",
    text:
      "Free zone enterprises enjoy competitive tax incentives that improve investor cash flow and project viability.",
    icon: Landmark,
  },
  {
    title: "Profit Repatriation",
    text:
      "Investors are permitted to repatriate profits and capital, supporting global investment confidence.",
    icon: Repeat2,
  },
];

const investorReasons = [
  "Ability to stock duty-free goods centrally for the West African region.",
  "Tax holidays and attractive profit repatriation benefits.",
  "Direct sea access for efficient cargo and offshore support operations.",
  "Single-point responsibility for logistics and operational coordination.",
  "Reduced double handling through integrated facilities and shared infrastructure.",
  "Efficiencies from common-user facilities and strategic oil and gas service bases.",
  "Opportunities across upstream, midstream and downstream oil and gas sectors.",
  "Strategic location for serving Nigeria and the wider West African market.",
  "Competitive tax concessions and minimal bureaucracy.",
  "Recognition as one of the largest and fastest-growing oil and gas transit bases in the world.",
];

const valueBlocks = [
  {
    label: "Speed",
    title: "Faster Movement of Goods",
    text:
      "Free zone operations reduce avoidable delays and support quicker cargo processing, project support and enterprise activity.",
    icon: Route,
  },
  {
    label: "Control",
    title: "One-Stop Operational Support",
    text:
      "OGFZA enables coordinated regulatory support, approvals and institutional supervision for enterprises operating within the zones.",
    icon: ShieldCheck,
  },
  {
    label: "Scale",
    title: "Regional Investment Platform",
    text:
      "The zones provide a launchpad for companies serving Nigeria, the Gulf of Guinea and the broader West African energy market.",
    icon: Globe2,
  },
];

const WhyOGFZ = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".benefit-card").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="Why OGFZ"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Investor Advantage
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Why OGFZ?
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Nigeria’s Oil and Gas Free Zones provide investors with strategic
            location advantages, regulatory support, tax incentives, logistics
            efficiency and access to regional oil and gas markets.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Why OGFZ</span>
          </div>
        </div>
      </section>

      {/* INVESTOR ADVANTAGE INTRO */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Why Investors Choose OGFZ
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Built For Speed, Scale, Logistics & Investment Confidence
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <p className="text-slate-700 leading-[1.9] text-[16px]">
              Oil and Gas Free Zones offer a specialized environment where
              investors can operate with stronger logistics support, attractive
              incentives, reduced bureaucracy and access to critical oil and gas
              infrastructure.
            </p>
          </div>

          <div className="reveal-up grid sm:grid-cols-3 gap-5">
            {valueBlocks.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.label}
                  className="rounded-[28px] p-7 min-h-[310px] flex flex-col justify-between bg-white border border-slate-200 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)] hover:-translate-y-2 hover:border-[#F2AF11]/60 transition-all duration-500"
                >
                  <div>
                    <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-6 border border-[#F2AF11]/25">
                      <Icon size={25} />
                    </div>

                    <p className="font-black tracking-[0.2em] uppercase text-xs mb-3 text-[#026B07]">
                      {item.label}
                    </p>

                    <h3
                      className="text-2xl font-black leading-tight mb-4 text-[#082567]"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>
                  </div>

                  <p className="leading-[1.75] text-slate-600 text-[15px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFIT MOSAIC */}
      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up max-w-4xl mx-auto text-center mb-14">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Free Zone Benefits
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              A More Competitive Environment For Oil & Gas Enterprises
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="benefit-card group relative overflow-hidden rounded-[30px] bg-white p-8 border border-slate-200 hover:-translate-y-2 transition-all duration-500 hover:shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)] hover:border-[#F2AF11]/60"
                >
                  <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-[#F2AF11]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-500" />

                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-[#082567]/5 text-[#082567] flex items-center justify-center mb-6 border border-[#082567]/10 group-hover:bg-[#F2AF11]/15 group-hover:border-[#F2AF11]/30 transition-all duration-500">
                      <Icon size={26} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight mb-4"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INVESTOR CHECKLIST */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Investor Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.07]"
        />
        <div className="absolute inset-0 bg-white/92" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-start">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Investor Reasons
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Why The Zones Remain Attractive To Serious Investors
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <p className="text-slate-700 leading-[1.9] text-[16px]">
              OGFZs combine incentives, infrastructure, market access and
              regulatory coordination to create an investor-friendly operating
              environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {investorReasons.map((item, index) => (
              <div
                key={index}
                className="reveal-up bg-white border border-slate-200 rounded-[24px] p-6 hover:-translate-y-2 hover:border-[#F2AF11]/60 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
              >
                <div className="flex gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={21} />
                  </div>

                  <p className="text-slate-700 leading-[1.75] text-[15.5px]">
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET ACCESS */}
      <section className="py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[36px] bg-white border border-slate-200 p-8 md:p-12 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/12 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/10 blur-3xl" />

            <div className="relative z-10 grid lg:grid-cols-[1fr_0.75fr] gap-10 items-center">
              <div className="reveal-up">
                <div className="inline-flex items-center gap-3 text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  <Layers3 size={18} className="text-[#F2AF11]" />
                  Strategic Advantage
                </div>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-5"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  A Strategic Gateway To Nigeria And West Africa’s Energy Market
                </h2>

                <p className="text-slate-700 leading-[1.9] text-[16px]">
                  OGFZs provide investors with the operational advantage of
                  proximity to oil and gas infrastructure, ports, logistics
                  corridors, offshore support systems and expanding industrial
                  opportunities.
                </p>
              </div>

              <div className="reveal-up bg-[#FAFBFC] rounded-[30px] p-7 border border-slate-200">
                <div className="w-16 h-16 rounded-[22px] bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center mb-6">
                  <Building2 size={30} />
                </div>

                <h3
                  className="text-3xl font-black text-[#082567] leading-tight mb-5"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Built for Investors. Structured for Growth.
                </h3>

                <Link
                  to="/about/incentives"
                  className="inline-flex items-center gap-3 mt-4 px-7 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500"
                >
                  View Incentives
                  <ArrowUpRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default WhyOGFZ;